import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { FiMail, FiPhone, FiMapPin } from 'react-icons/fi'
import Logo from '../brand/Logo'
import { renderWithBrand } from '../brand/Brand'
import { COMPANY } from '../../lib/company'

const currentYear = new Date().getFullYear()

// 내부 링크 아이템 타입
interface FooterLinkInternal {
  label: string
  to: string
  external?: false
}

// 외부 링크 아이템 타입
interface FooterLinkExternal {
  label: string
  to: string
  external: true
}

type FooterLink = FooterLinkInternal | FooterLinkExternal

export default function Footer() {
  const { t } = useTranslation()

  const productLinks: FooterLink[] = [
    { label: t('footer.exploreFeatures'), to: '/#features' },
    { label: t('nav.pricing'), to: '/pricing' },
    { label: t('nav.blog'), to: '/blog' },
    { label: t('nav.contact'), to: '/contact' },
  ]

  // ── 9개 산업안전 AI 기능 SEO 랜딩 cross-link (검색엔진 내부링크 강화) ──
  const aiFeatureLinks: FooterLinkInternal[] = [
    { label: t('featurePages.jsa.heroBadge'),               to: '/features/jsa' },
    { label: t('featurePages.tbm.heroBadge'),               to: '/features/tbm' },
    { label: t('featurePages.safetyEducation.heroBadge'),   to: '/features/safety-education' },
    { label: t('featurePages.workPermit.heroBadge'),        to: '/features/work-permit' },
    { label: t('featurePages.accidentReport.heroBadge'),    to: '/features/accident-report' },
    { label: t('featurePages.chatbot.heroBadge'),           to: '/features/chatbot' },
    { label: t('featurePages.riskAssessment.heroBadge'),    to: '/features/risk-assessment' },
    { label: t('featurePages.imageAnalysis.heroBadge'),     to: '/features/image-analysis' },
    { label: t('featurePages.corrective.heroBadge'),        to: '/features/corrective' },
    { label: t('featurePages.eta.heroBadge'),               to: '/features/eta' },
    { label: t('featurePages.fta.heroBadge'),               to: '/features/fta' },
    { label: t('featurePages.safetyCost.heroBadge'),        to: '/features/safety-cost' },
  ]

  const companyLinks: FooterLink[] = [
    { label: t('nav.about'), to: '/about' },
    { label: t('nav.contact'), to: '/contact' },
  ]

  const resourceLinks: FooterLink[] = [
    { label: t('footer.column'), to: '/blog' },
    { label: t('nav.faq'), to: '/contact#faq' },
    { label: t('nav.help'), to: '/contact' },
  ]

  const legalLinks: FooterLink[] = [
    { label: t('footer.privacyPolicy'), to: '/privacy' },
    { label: t('footer.termsOfService'), to: '/terms' },
  ]

  return (
    <footer className="bg-[#030508] border-t border-white/[0.08]">
      {/* 메인 링크 그리드 */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-16 pb-10">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-10">

          {/* 로고 + 설명 (2칼럼 차지) */}
          <div className="col-span-2 md:col-span-3 lg:col-span-2">
            <Logo size="md" className="mb-4" />
            <p className="text-sm text-ink-faint leading-relaxed max-w-xs mb-6">
              {t('footer.tagline')}
            </p>
            <div className="flex flex-col gap-2.5 text-sm text-ink-faint">
              <a
                href={`mailto:${COMPANY.email}`}
                className="inline-flex items-center gap-2 hover:text-amber-300 transition-colors duration-200"
              >
                <FiMail size={13} className="shrink-0 text-amber-500/60" aria-hidden="true" />
                {COMPANY.email}
              </a>
              <a
                href={`tel:${COMPANY.phone.replace(/-/g, '')}`}
                className="inline-flex items-center gap-2 hover:text-zinc-400 transition-colors duration-200"
              >
                <FiPhone size={13} className="shrink-0 text-zinc-600" aria-hidden="true" />
                {COMPANY.phone}
              </a>
              <span className="inline-flex items-start gap-2">
                <FiMapPin size={13} className="shrink-0 text-ink-faint mt-0.5" aria-hidden="true" />
                <span className="leading-relaxed">{COMPANY.address}</span>
              </span>
            </div>
          </div>

          {/* 제품 */}
          <div>
            <h3 className="text-xs font-semibold tracking-widest uppercase mb-4 bg-gradient-to-r from-amber-400/60 to-amber-400/60 bg-clip-text text-transparent">
              {t('footer.product')}
            </h3>
            <FooterLinkList links={productLinks} />
          </div>

          {/* 회사 */}
          <div>
            <h3 className="text-xs font-semibold tracking-widest uppercase mb-4 text-zinc-500">
              {t('footer.company')}
            </h3>
            <FooterLinkList links={companyLinks} />
          </div>

          {/* 자료 */}
          <div>
            <h3 className="text-xs font-semibold tracking-widest uppercase mb-4 text-zinc-500">
              {t('footer.resources')}
            </h3>
            <FooterLinkList links={resourceLinks} />
          </div>

          {/* 법적 */}
          <div>
            <h3 className="text-xs font-semibold tracking-widest uppercase mb-4 text-zinc-500">
              {t('footer.legal')}
            </h3>
            <FooterLinkList links={legalLinks} />
          </div>
        </div>
      </div>

      {/* AI 기능 cross-link strip — SEO 내부 링크 강화 */}
      <div className="border-t border-white/[0.04]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-4">
          <p className="text-xs text-ink-faint leading-relaxed flex flex-wrap items-center gap-x-3 gap-y-1.5">
            <span className="font-semibold text-zinc-500 mr-1">AI 기능</span>
            {aiFeatureLinks.map((link, i) => (
              <span key={link.to} className="inline-flex items-center gap-3">
                <Link
                  to={link.to}
                  className="text-zinc-500 hover:text-amber-300 transition-colors duration-200"
                >
                  {link.label}
                </Link>
                {i < aiFeatureLinks.length - 1 && <span className="opacity-30">·</span>}
              </span>
            ))}
          </p>
        </div>
      </div>

      {/* 사업자 정보 스트립 */}
      <div className="border-t border-white/[0.04]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-4">
          <p className="text-xs text-ink-faint leading-relaxed">
            <span className="text-ink-faint/60">{COMPANY.name}</span>
            <span className="mx-2 opacity-30">·</span>
            {t('footer.ceo')}: {COMPANY.ceo}
            <span className="mx-2 opacity-30">·</span>
            {t('footer.brn')}: {COMPANY.brn}
            <span className="mx-2 opacity-30">·</span>
            통신판매업신고: {COMPANY.tsreg_number}
          </p>
        </div>
      </div>

      {/* POWERED BY 스트립 */}
      <div className="border-t border-white/[0.04] bg-surface-deep/60">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <p className="text-xs text-ink-faint">
            {renderWithBrand(t('footer.copyright', { defaultValue: `© ${currentYear} Heinrich. All rights reserved.` }))}
          </p>

          <div className="flex items-center gap-3">
            <span className="text-[10px] font-semibold tracking-[0.15em] text-ink-faint/40 uppercase">
              {t('footer.poweredBy')}
            </span>
            <a
              href="https://dasancms.co.kr/"
              target="_blank"
              rel="noopener noreferrer"
              className="opacity-60 hover:opacity-100 transition-opacity duration-200"
              aria-label="다산 CMS 홈페이지"
            >
              <img
                src="/assets/dasan-cms-logo.png"
                alt="Dasan CMS"
                className="h-6 w-auto object-contain"
                loading="lazy"
              />
            </a>
          </div>
        </div>
      </div>
    </footer>
  )
}

// 재사용 가능한 링크 목록 서브컴포넌트
function FooterLinkList({ links }: { links: FooterLink[] }) {
  return (
    <ul className="flex flex-col gap-2.5">
      {links.map((link) => (
        <li key={`${link.to}-${link.label}`}>
          {link.external ? (
            <a
              href={link.to}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-zinc-500 hover:text-amber-300 transition-colors duration-200"
            >
              {link.label}
            </a>
          ) : (
            <Link
              to={link.to}
              className="text-sm text-zinc-500 hover:text-amber-300 transition-colors duration-200"
            >
              {link.label}
            </Link>
          )}
        </li>
      ))}
    </ul>
  )
}
